import React, { useState } from 'react';
import { Typography, Button, Stack, Box, IconButton, Menu, MenuItem, Drawer, Collapse } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../context/TranslationContext';
import TranslatedText from '../TranslatedText/TranslatedText';
import { getSectionPadding, getContainerStyles, getHeadingStyles } from '../../utils/responsiveStyles';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import AccountBoxOutlinedIcon from '@mui/icons-material/AccountBoxOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import BlurCircularOutlinedIcon from '@mui/icons-material/BlurCircularOutlined';
import AddAPhotoOutlinedIcon from '@mui/icons-material/AddAPhotoOutlined';
import SportsRugbyOutlinedIcon from '@mui/icons-material/SportsRugbyOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// Flag Components
const SpanishFlag: React.FC<{ width?: number; height?: number }> = ({ width = 20, height = 15 }) => (
  <svg width={width} height={height} viewBox="0 0 20 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="20" height="15" rx="2" fill="#C60B1E"/>
    <rect y="3.75" width="20" height="7.5" fill="#FFC400"/>
    <rect y="11.25" width="20" height="3.75" fill="#C60B1E"/>
  </svg>
);

const AmericanFlag: React.FC<{ width?: number; height?: number }> = ({ width = 20, height = 15 }) => (
  <svg width={width} height={height} viewBox="0 0 20 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="20" height="15" rx="2" fill="#B22234"/>
    <rect y="1.15" width="20" height="1.15" fill="#FFFFFF"/>
    <rect y="3.46" width="20" height="1.15" fill="#FFFFFF"/>
    <rect y="5.77" width="20" height="1.15" fill="#FFFFFF"/>
    <rect y="8.08" width="20" height="1.15" fill="#FFFFFF"/>
    <rect y="10.38" width="20" height="1.15" fill="#FFFFFF"/>
    <rect y="12.69" width="20" height="1.15" fill="#FFFFFF"/>
    <rect width="8" height="8.08" fill="#3C3B6E"/>
  </svg>
);

// Header styled components  
const TopBar = styled('div')(({ theme }) => ({
  backgroundColor: '#455b40',
  padding: '2px 0',
  borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
  width: '100vw',
  position: 'relative',
  left: '50%',
  right: '50%',
  marginLeft: '-50vw',
  marginRight: '-50vw',
  // Responsive padding
  [theme.breakpoints.down('xl')]: {
    padding: '3px 0'
  },
  [theme.breakpoints.down('lg')]: {
    padding: '4px 0'
  },
  [theme.breakpoints.down('md')]: {
    display: 'none'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    padding: '6px 0'
  }
}));

const MainHeader = styled('div')(({ theme }) => ({
  backgroundColor: 'transparent',
  padding: '0px 0',
  width: '100vw',
  position: 'relative',
  left: '50%',
  right: '50%',
  marginLeft: '-50vw',
  marginRight: '-50vw',
  [theme.breakpoints.down('md')]: {
    padding: '10px 0'
  }
}));



const NavButton = styled(Button)(({ theme }) => ({
  color: '#2a3a28',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '1.8rem',
  fontFamily: '"Lora", serif',
  padding: '10px 16px',
  minWidth: '140px', // Ensures consistent button width for both languages
  borderRadius: '0',
  whiteSpace: 'nowrap', // Prevent text wrapping
  textAlign: 'center', // Center text within the button
  // Ultra-wide screens need smaller text
  '@media (min-width: 1920px)': {
    fontSize: '1.7rem',
    padding: '10px 14px'
  },
  // Large screens (1536px+) 
  '@media (min-width: 1536px) and (max-width: 1920px)': {
    fontSize: '1.65rem',
    padding: '9px 13px'
  },
  [theme.breakpoints.down('xl')]: {
    fontSize: '1.6rem',
    padding: '8px 12px'
  },
  [theme.breakpoints.down('lg')]: {
    fontSize: '1.4rem',
    padding: '8px 10px'
  },
  [theme.breakpoints.between('md', 'lg')]: {
    fontSize: '1.2rem',
    padding: '6px 8px'
  },
  '&:hover': {
    backgroundColor: 'rgba(42, 58, 40, 0.05)',
    color: '#2a3a28',
    borderRadius: '0'
  }
}));

const DropdownMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    marginTop: '8px',
    minWidth: '280px'
  },
  '& .MuiList-root': {
    padding: '8px 0'
  }
}));

const DropdownMenuItem = styled(MenuItem)(({ theme }) => ({
  color: '#2a3a28',
  fontSize: '1.2rem',
  fontFamily: '"Lora", serif',
  fontWeight: 500,
  padding: '14px 24px',
  minHeight: '48px',
  '&:hover': {
    backgroundColor: '#2a3a28',
    color: '#FFFFFF'
  }
}));

const ContactInfo = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '24px',
  color: '#FFFFFF',
  fontSize: '14px',
  // Responsive gaps and font sizes
  [theme.breakpoints.down('xl')]: {
    gap: '20px',
    fontSize: '13px'
  },
  [theme.breakpoints.down('lg')]: {
    gap: '16px',
    fontSize: '12px'
  },
  [theme.breakpoints.down('md')]: {
    gap: '12px',
    fontSize: '11px'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    gap: '28px',
    fontSize: '15px'
  }
}));

const ContactItem = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '6px'
}));

const SocialIcons = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: '12px'
}));

const SocialIcon = styled(IconButton)(({ theme }) => ({
  color: '#c2b8a8',
  fontSize: '18px',
  padding: '8px',
  minWidth: '44px',
  minHeight: '44px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)'
  },
  '&:active': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)'
  },
  // Responsive sizing
  [theme.breakpoints.down('xl')]: {
    fontSize: '17px',
    minWidth: '42px',
    minHeight: '42px',
    padding: '7px'
  },
  [theme.breakpoints.down('lg')]: {
    fontSize: '16px',
    minWidth: '40px',
    minHeight: '40px',
    padding: '6px'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '15px',
    minWidth: '38px',
    minHeight: '38px',
    padding: '5px'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    fontSize: '20px',
    minWidth: '48px',
    minHeight: '48px',
    padding: '10px'
  }
}));

const LetsTalkButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#85b47c',
  color: '#FFFFFF',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1.2rem',
  fontFamily: '"Lora", serif',
  padding: '8px 24px',
  borderRadius: '12px',
  border: `2px solid #85b47c`,
  position: 'relative',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  minHeight: '44px',
  touchAction: 'manipulation',
  // Responsive sizing for larger screens
  '@media (min-width: 1920px)': {
    fontSize: '1.3rem',
    padding: '10px 28px'
  },
  '@media (min-width: 1536px) and (max-width: 1920px)': {
    fontSize: '1.25rem',
    padding: '9px 26px'
  },
  '& .MuiButton-startIcon, & .MuiButton-endIcon, & .MuiButton-label': {
    position: 'relative',
    zIndex: 2
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    backgroundColor: '#FFFFFF',
    transition: 'left 0.4s ease',
    zIndex: 1,
    borderRadius: '12px'
  },
  '&::after': {
    content: '"Let\'s Talk →"',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: '#85b47c',
    fontWeight: 600,
    fontSize: '1.2rem',
    fontFamily: '"Lora", serif',
    zIndex: 3,
    opacity: 0,
    transition: 'opacity 0.4s ease',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  '&:hover': {
    borderColor: '#85b47c',
    backgroundColor: '#85b47c',
    '&::before': {
      left: 0
    },
    '&::after': {
      opacity: 1
    },
    '& span': {
      opacity: 0
    }
  }
}));

const HeroContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column'
}));

const HeroImageSection = styled(Box)(({ theme }) => ({
  ...getContainerStyles(theme),
  maxWidth: '1900px',
  height: '700px',
  position: 'relative',
  overflow: 'hidden',
  background: 'linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.5)), url("/hero.jpg")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  borderTopLeftRadius: '30px',
  borderTopRightRadius: '30px',
  marginTop: '100px', // TopBar (~40px) + MainHeader (~85px) = ~125px

  [theme.breakpoints.down('lg')]: {
    height: '600px',
  },
  [theme.breakpoints.down('md')]: {
    height: '500px',
    borderTopLeftRadius: '20px',
    borderTopRightRadius: '20px',
    marginTop: '75px'
  },
  [theme.breakpoints.down('sm')]: {
    height: '480px',
    borderTopLeftRadius: '15px',
    borderTopRightRadius: '15px',
    marginTop: '70px'
  },
  '@media (max-width: 480px)': {
    height: '460px',
    marginTop: '70px'
  }
}));

const ContentContainer = styled(Stack)(({ theme }) => ({
  maxWidth: '1200px',
  margin: '0',
  marginLeft: '80px',
  padding: '80px 40px 0 0',
  height: '100%',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  position: 'relative',
  zIndex: 10,
  [theme.breakpoints.down('lg')]: {
    marginLeft: '60px',
    padding: '60px 30px 0 0'
  },
  [theme.breakpoints.down('md')]: {
    marginLeft: '40px',
    padding: '40px 20px 0 0'
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: '20px',
    padding: '5px 15px 30px 0'
  },
  '@media (max-width: 480px)': {
    marginLeft: '15px',
    padding: '5px 10px 25px 0'
  }
}));

const LeftContent = styled(Stack)(({ theme }) => ({
  flex: 1,
  gap: '32px',
  alignItems: 'flex-start',
  [theme.breakpoints.down('md')]: {
    gap: '24px' // Reduce gap on tablets
  },
  [theme.breakpoints.down('sm')]: {
    gap: '12px' // Tighter spacing on mobile
  },
  '@media (max-width: 480px)': {
    gap: '10px' // Very tight on small screens
  }
}));

const RightContent = styled(Box)(({ theme }) => ({
  flex: 1,
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

const MainHeading = styled(Typography)(({ theme }) => ({
  fontSize: '5.5rem',
  fontFamily: '"Lora", san-serif',
  fontWeight: 700,
  lineHeight: 1.1,
  color: '#FFFFFF',
  textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
  marginBottom: '16px',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('lg')]: {
    fontSize: '4.5rem'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '3.5rem'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '2.5rem',
    whiteSpace: 'normal',
    marginBottom: '0px'
  },
  '@media (max-width: 480px)': {
    fontSize: '2.2rem',
    lineHeight: 1.2,
    marginBottom: '0px'
  }
}));

const SubHeading = styled(Typography)(({ theme }) => ({
  fontSize: '1.2rem',
  fontFamily: '"Lora", serif',
  color: 'rgba(255, 255, 255, 0.9)',
  lineHeight: 1.6,
  marginBottom: '24px',
  maxWidth: '500px',
  [theme.breakpoints.down('md')]: {
    fontSize: '1.1rem',
    marginBottom: '20px',
    maxWidth: '400px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    marginBottom: '0px',
    maxWidth: '350px'
  },
  '@media (max-width: 480px)': {
    fontSize: '0.95rem',
    marginBottom: '0px',
    maxWidth: '300px'
  }
}));

const SeparationLine = styled('div')(({ theme }) => ({
  width: '500px',
  height: '3px',
  backgroundColor: theme.palette.primary.main,
  marginBottom: '32px',
  [theme.breakpoints.down('lg')]: {
    width: '400px'
  },
  [theme.breakpoints.down('md')]: {
    width: '300px'
  },
  [theme.breakpoints.down('sm')]: {
    width: '250px',
    marginBottom: '0px'
  },
  '@media (max-width: 480px)': {
    width: '200px',
    marginBottom: '0px'
  }
}));

const CTAButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#455b40',
  color: '#FFFFFF',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1.1rem',
  fontFamily: '"Lora", serif',
  padding: '16px 32px',
  borderRadius: '30px',
  marginRight: '16px',
  border: `2px solid ${theme.palette.primary.main}`,
  position: 'relative',
  overflow: 'hidden',
  minHeight: '48px',
  touchAction: 'manipulation',
  '& .MuiButton-startIcon, & .MuiButton-endIcon, & .MuiButton-label': {
    position: 'relative',
    zIndex: 2
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    backgroundColor: '#FFFFFF',
    transition: 'left 0.4s ease',
    zIndex: 1,
    borderRadius: '30px'
  },
  '&::after': {
    content: '"Descubre nuestros cursos  →"',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: '#455b40',
    fontWeight: 600,
    fontSize: '1.1rem',
    fontFamily: '"Lora", serif',
    zIndex: 3,
    opacity: 0,
    transition: 'opacity 0.4s ease',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  '&:hover': {
    borderColor: theme.palette.primary.main,
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(194, 184, 168, 0.4)',
    '&::before': {
      left: 0
    },
    '&::after': {
      opacity: 1
    },
    '& span': {
      opacity: 0
    }
  },
  transition: 'all 0.4s ease'
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  color: '#FFFFFF',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1.1rem',
  fontFamily: '"Lora", serif',
  padding: '16px 32px',
  borderRadius: '30px',
  border: '2px solid rgba(255, 255, 255, 0.3)',
  position: 'relative',
  overflow: 'hidden',
  minHeight: '48px',
  touchAction: 'manipulation',
  '& .MuiButton-startIcon, & .MuiButton-endIcon, & .MuiButton-label': {
    position: 'relative',
    zIndex: 2
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    backgroundColor: '#455b40',
    transition: 'left 0.4s ease',
    zIndex: 1,
    borderRadius: '30px'
  },
  '&::after': {
    content: '"Granadas Ecológicas →"',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: '#FFFFFF',
    fontWeight: 600,
    fontSize: '1.1rem',
    fontFamily: '"Lora", serif',
    zIndex: 3,
    opacity: 0,
    transition: 'opacity 0.4s ease',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  '&:hover': {
    borderColor: theme.palette.primary.main,
    transform: 'translateY(-2px)',
    '&::before': {
      left: 0
    },
    '&::after': {
      opacity: 1
    },
    '& span': {
      opacity: 0
    }
  },
  transition: 'all 0.4s ease'
}));

// Mobile Menu Styles
const MobileMenuButton = styled(IconButton)(({ theme }) => ({
  display: 'none',
  color: '#2a3a28',
  padding: '12px',
  minWidth: '48px',
  minHeight: '48px',
  '&:hover': {
    backgroundColor: 'rgba(42, 58, 40, 0.05)'
  },
  '&:active': {
    backgroundColor: 'rgba(42, 58, 40, 0.1)'
  },
  [theme.breakpoints.down('md')]: {
    display: 'flex'
  }
}));

const DesktopNav = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '12px',
  [theme.breakpoints.down('xl')]: {
    gap: '8px'
  },
  [theme.breakpoints.down('lg')]: {
    gap: '6px'
  },
  [theme.breakpoints.between('md', 'lg')]: {
    gap: '4px'
  },
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const MobileDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: '280px',
    backgroundColor: '#FFFFFF',
    padding: '20px 0',
    // Responsive drawer width
    [theme.breakpoints.down('sm')]: {
      width: '260px',
      padding: '16px 0'
    },
    '@media (max-width: 480px)': {
      width: '240px',
      padding: '12px 0'
    },
    // Ultra-wide screens
    '@media (min-width: 1920px)': {
      width: '320px',
      padding: '24px 0'
    }
  }
}));

const MobileNavItem = styled(Button)(({ theme }) => ({
  color: '#2a3a28',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '1.4rem',
  fontFamily: '"Lora", serif',
  padding: '16px 24px',
  justifyContent: 'space-between',
  width: '100%',
  borderRadius: 0,
  minHeight: '56px',
  touchAction: 'manipulation',
  '&:hover': {
    backgroundColor: 'rgba(42, 58, 40, 0.05)'
  },
  '&:active': {
    backgroundColor: 'rgba(42, 58, 40, 0.1)'
  },
  // Responsive sizing
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.3rem',
    padding: '14px 20px',
    minHeight: '52px'
  },
  '@media (max-width: 480px)': {
    fontSize: '1.2rem',
    padding: '12px 18px',
    minHeight: '48px'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    fontSize: '1.5rem',
    padding: '18px 28px',
    minHeight: '60px'
  }
}));

const MobileSubNavItem = styled(Button)(({ theme }) => ({
  color: '#2a3a28',
  textTransform: 'none',
  fontWeight: 400,
  fontSize: '1.2rem',
  fontFamily: '"Lora", serif',
  padding: '12px 40px',
  justifyContent: 'flex-start',
  width: '100%',
  borderRadius: 0,
  minHeight: '48px',
  touchAction: 'manipulation',
  backgroundColor: 'rgba(42, 58, 40, 0.02)',
  '&:hover': {
    backgroundColor: 'rgba(42, 58, 40, 0.08)'
  },
  '&:active': {
    backgroundColor: 'rgba(42, 58, 40, 0.12)'
  },
  // Responsive sizing
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
    padding: '10px 36px',
    minHeight: '44px'
  },
  '@media (max-width: 480px)': {
    fontSize: '1rem',
    padding: '8px 32px',
    minHeight: '40px'
  },
  // Ultra-wide screens
  '@media (min-width: 1920px)': {
    fontSize: '1.3rem',
    padding: '14px 44px',
    minHeight: '52px'
  }
}));

const ResponsiveLogoContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '120px',
  height: '110px',
  left: '50%',
  transform: 'translateX(-100px)', // Perfect position for desktop
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 25,
  pointerEvents: 'none',
  top: '-5px',
  borderRadius: '0px 0px 20px 20px',
  overflow: 'hidden',
  // Ultra-wide screens (1920px+)
  '@media (min-width: 1920px)': {
    transform: 'translateX(-75px)' // Your new baseline for ultra-wide
  },
  // Large desktop (1700-1920px)
  '@media (min-width: 1700px) and (max-width: 1920px)': {
    transform: 'translateX(-80px)' // Slightly closer for this range
  },
  // Medium-large desktop (1536-1700px)
  [theme.breakpoints.down('xl')]: {
    transform: 'translateX(-85px)' // Adjusted based on -75px baseline
  },
  // Specific fix for 1550-1650px problematic range  
  '@media (min-width: 1550px) and (max-width: 1650px)': {
    transform: 'translateX(-83px)' // Fine-tune for this specific range
  },
  // Medium desktop (1200-1536px) 
  [theme.breakpoints.down('lg')]: {
    width: '110px',
    height: '95px',
    transform: 'translateX(-75px)' // Consistent with your new baseline
  },
  // Small laptop (1024-1200px)
  '@media (max-width: 1200px)': {
    width: '100px',
    height: '90px',
    transform: 'translateX(-70px)' // Slightly closer for smaller screens
  },
  // Tablet landscape (900-1024px)
  '@media (max-width: 1024px)': {
    width: '95px',
    height: '85px',
    transform: 'translateX(-65px)' // Proportional adjustment
  },
  // Tablet portrait (768-900px)
  '@media (max-width: 900px) and (min-width: 768px)': {
    width: '85px',
    height: '75px',
    transform: 'translateX(-60px)' // Smaller screens, closer position
  },
  // Small tablet (600-768px) - still show logo but smaller
  '@media (max-width: 768px) and (min-width: 600px)': {
    width: '75px',
    height: '65px',
    transform: 'translateX(-55px)', // Adjusted for your new baseline
    top: '0px'
  },
  // Hide logo when hamburger menu appears (same breakpoint as mobile menu)
  [theme.breakpoints.down('md')]: {
    display: 'none' // Hide logo when hamburger menu appears at 900px
  }
}));

const DecorationLine = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: '100px',
  left: '50%',
  transform: 'translateX(-50%)',
  width: '200px',
  height: '3px',
  background: 'linear-gradient(90deg, transparent, #FFFFFF, transparent)',
  opacity: 0.3
}));

const FloatingElement = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '100px',
  height: '100px',
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  animation: 'float 6s ease-in-out infinite',
  '@keyframes float': {
    '0%, 100%': {
      transform: 'translateY(0px)'
    },
    '50%': {
      transform: 'translateY(-20px)'
    }
  }
}));

const HeroSection: React.FC = () => {
  const { currentLanguage, setLanguage } = useTranslation();
  const [sobreNosotrosAnchor, setSobreNosotrosAnchor] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSobreNosotrosOpen, setMobileSobreNosotrosOpen] = useState(false);
  const [languageAnchor, setLanguageAnchor] = useState<null | HTMLElement>(null);

  const handleSobreNosotrosClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setSobreNosotrosAnchor(event.currentTarget);
  };

  const handleClose = () => {
    setSobreNosotrosAnchor(null);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
    setMobileSobreNosotrosOpen(false);
  };

  const handleMobileSobreNosotrosToggle = () => {
    setMobileSobreNosotrosOpen(!mobileSobreNosotrosOpen);
  };

  const handleLanguageClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setLanguageAnchor(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setLanguageAnchor(null);
  };

  const handleLanguageSelect = (language: 'ES' | 'EN') => {
    setLanguage(language);
    setLanguageAnchor(null);
  };


  return (
    <HeroContainer>
      {/* Hero Image Section - Now starts immediately, with padding for fixed header */}
      <HeroImageSection>
        {/* Floating decorative elements */}
        <FloatingElement sx={{ top: '20%', left: '10%', animationDelay: '0s' }} />
        <FloatingElement sx={{ top: '60%', right: '15%', animationDelay: '2s' }} />
        <FloatingElement sx={{ bottom: '20%', left: '5%', animationDelay: '4s' }} />
        
        <ContentContainer direction="row" spacing={0}>
          <LeftContent>
          <MainHeading component="h1">
          <TranslatedText>Cultivamos salud</TranslatedText><br />
          <TranslatedText>desde el suelo</TranslatedText><br />
          </MainHeading>
          
          <SubHeading>
          <TranslatedText>Agricultura ecológica y alimentos orgánicos accesibles desde nuestra finca en Bétera, Valencia.</TranslatedText>
          </SubHeading>
          
          <SeparationLine />
          
          <Stack 
            direction="row" 
            alignItems="center"
            sx={{
              '@media (max-width: 900px)': {
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '16px',
                width: '100%'
              }
            }}
          >
            <Link to="/cursos" style={{ textDecoration: 'none' }}>
              <CTAButton
                sx={{
                  '@media (max-width: 900px)': {
                    width: '100%',
                    maxWidth: '320px'
                  },
                  '@media (max-width: 600px)': {
                    fontSize: '0.9rem',
                    padding: '12px 16px',
                    whiteSpace: 'nowrap' // Prevent text wrapping
                  },
                  '@media (max-width: 480px)': {
                    fontSize: '0.85rem',
                    padding: '10px 14px'
                  }
                }}
              >
              <TranslatedText>Descubre nuestros cursos →</TranslatedText>
              </CTAButton>
            </Link>
            <Link to="/granadas-ecologicas" style={{ textDecoration: 'none' }}>
              <SecondaryButton
                sx={{
                  '@media (max-width: 900px)': {
                    width: '100%',
                    maxWidth: '320px'
                  },
                  '@media (max-width: 600px)': {
                    fontSize: '0.9rem',
                    padding: '12px 16px',
                    whiteSpace: 'nowrap' // Prevent text wrapping
                  },
                  '@media (max-width: 480px)': {
                    fontSize: '0.85rem',
                    padding: '10px 14px'
                  }
                }}
              >
              <TranslatedText>Granadas Ecológicas →</TranslatedText>
              </SecondaryButton>
            </Link>
          </Stack>
        </LeftContent>

        <RightContent>
          {/* Decorative curved line */}
          <svg
            style={{
              position: 'absolute',
              bottom: '50px',
              left: '-50px',
              width: '200px',
              height: '100px',
              opacity: 0.6
            }}
            viewBox="0 0 200 100"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 80 Q 100 20 190 60"
              stroke="#FFFFFF"
              strokeWidth="3"
              fill="none"
              strokeLinecap="round"
            />
          </svg>
        </RightContent>
      </ContentContainer>
        
        <DecorationLine />
      </HeroImageSection>
    </HeroContainer>
  );
};

export default HeroSection;